<script lang="ts">
  import { onDestroy } from 'svelte';
  import { date } from 'svelte-i18n';
  import { Button } from "$lib/components/ui/button";
  import * as Card from "$lib/components/ui/card";
  import WhatsAppIcon from '../WhatsAppIcon.svelte';
  import { getLinkCode, unlink as unlinkRequest } from './whatsapp-api';
  import type { LinkCode } from './whatsapp-api';

  export let linked: boolean;
  export let waIdMasked: string | null = null;
  export let linkedAt: string | null = null;
  export let businessNumber: string | null = null;

  // Formats a plain digit string (e.g. "18665204762") as "+1 (866) 520-4762" for display.
  // Falls back to a plain "+" prefix for shapes that don't match the 10/11-digit NANP pattern.
  function formatPhoneNumber(value: string | null): string | null {
    if (!value) return value;
    const digits = value.replace(/[^0-9]/g, '');

    if (digits.length === 11 && digits[0] === '1') {
      const rest = digits.slice(1);
      return `+1 (${rest.slice(0, 3)}) ${rest.slice(3, 6)}-${rest.slice(6)}`;
    }
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    if (digits.length === 0) return null;
    return `+${digits}`;
  }

  $: formattedBusinessNumber = formatPhoneNumber(businessNumber);

  let pendingCode: LinkCode | null = null;
  let loading = false;
  let error: string | null = null;

  async function requestCode() {
    loading = true;
    error = null;
    try {
      pendingCode = await getLinkCode();
    } catch (e) {
      error = 'Could not generate a code, please try again.';
    } finally {
      loading = false;
    }
  }

  async function disconnect() {
    loading = true;
    error = null;
    try {
      await unlinkRequest();
      linked = false;
      waIdMasked = null;
      linkedAt = null;
      pendingCode = null;
    } catch (e) {
      error = 'Could not disconnect, please try again.';
    } finally {
      loading = false;
    }
  }

  //@ts-ignore
  const Xert = window.Xert;
  const channel = Xert?._userChannel;

  const onLinked = (event: { wa_id_masked: string; linked_at: string }) => {
    linked = true;
    waIdMasked = event.wa_id_masked;
    linkedAt = event.linked_at;
    pendingCode = null;
  };

  if (channel) {
    channel.bind('whatsapp_linked', onLinked);
  }
  onDestroy(() => {
    if (channel) {
      channel.unbind('whatsapp_linked', onLinked);
    }
  });
</script>

<div class="flex flex-col prose gap-2">
  <!-- Shown in both the linked and unlinked states -- what gets relayed over WhatsApp is only
  coach/athlete messaging (direct messages and activity/day comments), not Xert notifications in
  general, which is the thing people assume this page is signing them up for. -->

  <p class="text-muted-foreground text-sm leading-snug m-0">
    <i class="fa-light fa-circle-info mr-1"></i>
    Messaging is for communicating with your coach or your athletes -- connecting WhatsApp relays
    those direct messages and activity comments to your phone. It doesn't send any other Xert
    notifications.
  </p>

  {#if linked}
    <Card.Root class="xl:col-span-2 relative">
      <Card.Header>
        <div class="flex flex-col gap-2">
          Your account is linked to WhatsApp: <strong>{waIdMasked}</strong>
          {#if linkedAt}
            <span class="text-muted-foreground text-xs font-normal leading-snug">
              Linked since {$date(new Date(linkedAt), { year: 'numeric', month: 'long', day: 'numeric' })}
            </span>
          {/if}
        </div>
      </Card.Header>
      <Card.Content>
        <Button variant="destructive" on:click={disconnect} disabled={loading}>
          <i class="fa fa-chain-broken mr-2" /> Disconnect
        </Button>
        {#if error}<p class="text-destructive text-sm mt-2">{error}</p>{/if}
      </Card.Content>
    </Card.Root>
  {:else}
    <Card.Root class="relative xl:col-span-2">
      <Card.Header>
        <Card.Title>Connect WhatsApp</Card.Title>
        <Card.Description>
          Get a message from Xert on WhatsApp whenever someone sends you a message in the app, and reply
          right from WhatsApp. To connect, generate a code below and send it to us on WhatsApp.
        </Card.Description>
        <Card.Description>
          By sending this code, you agree to receive WhatsApp messages from Xert related to your account
          activity. You can disconnect at any time from this page.
        </Card.Description>
      </Card.Header>
      <Card.Content>
        {#if pendingCode}
          <div class="flex flex-col gap-2">
            <p>Send this code to us on WhatsApp {#if formattedBusinessNumber}at <strong>{formattedBusinessNumber}</strong>{/if}:</p>
            <p class="text-2xl font-mono font-bold">{pendingCode.code}</p>
            {#if pendingCode.wa_me_link}
              <Button variant="default" href={pendingCode.wa_me_link} target="_blank">
                <WhatsAppIcon size={16} title="WhatsApp" class="mr-2 rounded-full bg-white p-px" /> Open WhatsApp with code filled in
              </Button>
            {/if}
            <p class="text-muted-foreground text-xs font-normal leading-snug">
              This code expires at {$date(new Date(pendingCode.expires_at), { hour: 'numeric', minute: 'numeric' })}.
              Waiting for confirmation&hellip;
            </p>
          </div>
        {:else}
          <Button variant="default" on:click={requestCode} disabled={loading}>
            <i class="fa fa-chain mr-2" /> Get a linking code
          </Button>
        {/if}
        {#if error}<p class="text-destructive text-sm mt-2">{error}</p>{/if}
      </Card.Content>
    </Card.Root>
  {/if}
</div>
